<script setup lang="ts">
//#region imports
import { ref, computed, nextTick, onBeforeMount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { differenceInHours } from 'date-fns';
import { useTypedStore } from '@/store';
import { useTableSort } from '@/composables/useTableLS';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useLs } from '@/setup/vueLs';
import { useApi } from '@/api';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetToolsPromoMatchesLeaguesData, GetToolsPromoMatchesLeaguesResponse } from '@/types/openapi-ts';
//#endregion

//#region const
const LS_PATH = 'LandingLeagues';

const fields: UiTableNewField<GetToolsPromoMatchesLeaguesResponse['payload'][number]>[] = [
  {
    name: 'name',
    align: 'left',
    sortable: 'custom',
    class: 'landing-leagues__league-name',
  },
  {
    name: 'last_starts_at',
    width: 120,
    align: 'left',
    sortable: 'custom',
    format: 'date-time',
    class: 'landing-leagues__table-last-match',
  },
  {
    name: 'matches_count',
    width: 125,
    align: 'right',
    sortable: 'custom',
  },
];
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const route = useRoute();
const router = useRouter();
const detectPermissions = useDetectPermissions();
const ls = useLs();
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'name',
    sort_dir: 'asc',
  },
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region state
const tableData = ref<GetToolsPromoMatchesLeaguesResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);

const currentBrand = ref('');
//#endregion

//#region computed
const brands = computed(() => store.getters['misc/brands']);

//#endregion

//#region methods
function initData() {
  currentBrand.value = route.query.site_id
    ? route.query.site_id
    : ls.get(`${LS_PATH}/site_id`) || brands.value[0]!.id;
  fetchData();
}

async function fetchData() {
  if (!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  const options = {
    ...optionsSort.value,
    site_id: currentBrand.value,
  } as GetToolsPromoMatchesLeaguesData['query'];
  try {
    const { data: { payload } } = await api.getLeagues(options);
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function getRowClass(row: GetToolsPromoMatchesLeaguesResponse['payload'][number]) {
  return row.last_starts_at
    ? differenceInHours(row.last_starts_at, new Date()) < 72
      ? 'landing-leagues__table-row-is-coming'
      : ''
    : '';
}

function handleRowClick(row: GetToolsPromoMatchesLeaguesResponse['payload'][number]) {
  router.push(`/tools/league/${row.id}/matches?site_id=${currentBrand.value}`);
}

function onSelectBrand(id: string) {
  currentBrand.value = id;
  fetchData();
}
//#endregion

//#region watch
watch(currentBrand, async (newVal) => {
  ls.set(`${LS_PATH}/site_id`, newVal);
});
//#endregion

//#region hooks
onBeforeMount(async () => {
  detectPermissions.checkRoute('tools_promo_landings_view');
  if (detectPermissions.checkRequest('tools_promo_landings_view')) {
    initData();
  }
});
//#endregion
</script>

<template>
  <div class="landing-leagues">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('tools.landingLeagues.leagues.land_leagues') }}
      </template>
    </UiToolbar>
    <BrandsList
      :current-brand="currentBrand"
      @brand-change="onSelectBrand"
    />
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      :computed-row-class="getRowClass"
      class="landing-leagues__table"
      lazy-loading
      is-static
      i18n-path="tools.landingLeagues.leagues"
      @sort="handleSort"
      @row-click="handleRowClick"
    >
      <template
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              :tooltip="$t('affiliates.monitored.infoButton')"
              @click="handleRowClick(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang="scss">
.landing-leagues {
  &__table-row-is-coming {
    td.ui-table__table-cell {
      background-color: var(--background-negative-subtle);
    }
  }
  @at-root #{selector-nest(& + '__table-row-is-coming', & + '__table-last-match')} {
    color: var(--text-negative);
  }
  &__league-name {
    color: var(--text-link);
    cursor: pointer;
  }
}
</style>
